'use client';

import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Marquee } from 'components/shared/marquee/Marquee';
import SphereOrb, { type OrbState } from 'components/science/landing/orb/SphereOrb';
import ButtonV2 from 'components/shared/ButtonV2';
import { GLASS_TIMING, type GlassLook } from './glassMarble/glassTiming';

const GlassMarble = dynamic(() => import('./glassMarble/GlassMarble'), { ssr: false });

import styles from './OrbBeat.module.css';

/* §8 — the orb beat (Figma 900:590 → 900:883). One scroll-pinned sequence: the
   ORB's own shader fills the viewport as a flat, constant-density marble FIELD
   (u_zoom = 1). On scroll a clip-path morphs the visible shape rectangle → card →
   circle, and u_zoom eases 1 → 0 so the field gains sphere lighting and BECOMES
   the orb — one canvas, no zoom, no crossfade. "The science, explained simply."
   (drifting explainer pills + stats + CTA) then assembles around it. ONLY once
   the orb is formed (progress ≥ 0.82) does it CYCLE through signal states — label,
   metrics (odometer roll) and material change like the app; the full-screen field
   and the morph stay blue, and scrolling back returns to blue. Progress is
   measured with getBoundingClientRect + rAF and eased; reduced-motion + mobile
   snap to the assembled end-state. */

type Topic = { label: string; slug: string };
type RGB = [number, number, number];
type Dir = 'up' | 'down';

/* the orb is a FIXED on-screen diameter (Figma sphere = 180px) — it does not
   scale with the display; on large screens the marquee/field just fills more
   width around it. */
const ORB_DIAMETER = 180;

type OrbLabelKey = 'sunlight' | 'recovered' | 'move' | 'windDown';
type StatKey = 'sleep' | 'recovery' | 'movement';

const BASE_ORB: OrbState = {
  heartRate: 60,
  redness: 0,
  green: 0,
  grayscale: 0,
  loading: 0,
  tint: [0.231, 0.51, 0.965],
  tintAmt: 0.34,
};

const CYCLE: {
  labelKey: OrbLabelKey;
  stats: { k: StatKey; v: number; dir: Dir }[];
  orb: Partial<OrbState>;
}[] = [
  {
    labelKey: 'sunlight',
    stats: [
      { k: 'sleep', v: 89, dir: 'up' },
      { k: 'recovery', v: 79, dir: 'up' },
      { k: 'movement', v: 65, dir: 'down' },
    ],
    orb: { tint: [0.231, 0.51, 0.965], tintAmt: 0.34, green: 0, redness: 0, heartRate: 60 },
  },
  {
    labelKey: 'recovered',
    stats: [
      { k: 'sleep', v: 82, dir: 'up' },
      { k: 'recovery', v: 93, dir: 'up' },
      { k: 'movement', v: 58, dir: 'down' },
    ],
    orb: { tint: [0.0, 0.47, 0.37], tintAmt: 0.34, green: 0.14, redness: 0, heartRate: 54 },
  },
  {
    labelKey: 'move',
    stats: [
      { k: 'sleep', v: 74, dir: 'down' },
      { k: 'recovery', v: 71, dir: 'up' },
      { k: 'movement', v: 44, dir: 'down' },
    ],
    orb: { tint: [0.851, 0.392, 0.204], tintAmt: 0.28, green: 0, redness: 0.12, heartRate: 70 },
  },
  {
    labelKey: 'windDown',
    stats: [
      { k: 'sleep', v: 68, dir: 'down' },
      { k: 'recovery', v: 76, dir: 'up' },
      { k: 'movement', v: 81, dir: 'up' },
    ],
    orb: { tint: [0.231, 0.51, 0.965], tintAmt: 0.34, green: 0, redness: 0, heartRate: 63 },
  },
];

/* stat trend triangle — green up (sleep/recovery), magenta down (movement).
   A same-colour round-joined stroke softens the three corners so it reads as a
   rounded triangle rather than a sharp one. */
const Tri = ({ dir }: { dir: Dir }) => {
  const color = dir === 'down' ? '#ab0756' : '#07ab8a';
  return (
    <svg
      className={styles.tri}
      width="11"
      height="10"
      viewBox="0 0 11 10"
      aria-hidden="true"
      style={dir === 'down' ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path
        d="M5.5 1.6L9.4 8.4H1.6L5.5 1.6Z"
        fill={color}
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const seg = (p: number, a: number, b: number) => clamp01((p - a) / (b - a));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
const easeOut = (t: number) => 1 - (1 - t) ** 3;

/* progress past which the orb is considered "formed" (on the phone) and may cycle */
const FORMED = 0.9;

export default function OrbBeat({ topics, bannerPx = 0 }: { topics: Topic[]; bannerPx?: number }) {
  const t = useTranslations('science');
  const line1 = t('science.landing.orb.headlineLine1');
  const line2 = t('science.landing.orb.headlineLine2');
  const headlineWords = [...line1.split(' '), ...line2.split(' ')];
  const headlineBreakAt = line1.split(' ').length;
  const orbLabel = {
    sunlight: t('science.landing.orb.labels.sunlight'),
    recovered: t('science.landing.orb.labels.recovered'),
    move: t('science.landing.orb.labels.move'),
    windDown: t('science.landing.orb.labels.windDown'),
  };
  const statLabel = {
    sleep: t('science.landing.orb.stats.sleep'),
    recovery: t('science.landing.orb.stats.recovery'),
    movement: t('science.landing.orb.stats.movement'),
  };
  const trackRef = useRef<HTMLElement | null>(null);
  const whiteRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const explainedRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef<number>(1); // 1 = flat field, 0 = sphere
  const progressRef = useRef<number>(0);
  const valRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const displayedRef = useRef<number[]>(CYCLE[0].stats.map((s) => s.v));
  const formedRef = useRef<boolean>(false);
  // the orb is a smaller fixed diameter on phones (Figma mobile 949:13971 sits it
  // inside a compact phone), full 180px on desktop. Set once on the first client
  // render so SphereOrb mounts at the right size (no re-mount → no WebGL reset).
  const [orbPx, setOrbPx] = useState<number>(() =>
    typeof window !== 'undefined' && window.innerWidth <= 640 ? 126 : ORB_DIAMETER,
  );

  /* WebGL recovery: the browser can drop a context at any time (GPU switch,
     memory pressure, a backgrounded tab on a phone). Neither canvas could draw
     again after that, which left the orb blank until a reload — so when either
     loses its context, both remount with fresh canvases a beat later. */
  const pinRef = useRef<HTMLDivElement | null>(null);
  const [glKey, setGlKey] = useState(0);
  useEffect(() => {
    const pin = pinRef.current;
    if (!pin) return undefined;
    let t: ReturnType<typeof setTimeout> | undefined;
    const onLost = (e: Event) => {
      e.preventDefault();
      clearTimeout(t);
      t = setTimeout(() => setGlKey((k) => k + 1), 250);
    };
    pin.addEventListener('webglcontextlost', onLost, true);
    return () => {
      clearTimeout(t);
      pin.removeEventListener('webglcontextlost', onLost, true);
    };
  }, []);
  const orbPxRef = useRef<number>(orbPx);
  // keep the orb the right size across a resize that crosses the phone breakpoint
  // (desktop stays 180, mobile 126); setOrbPx only re-renders when it actually
  // changes, so the sphere re-mounts only on a real breakpoint crossing
  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth <= 640 ? 126 : ORB_DIAMETER;
      orbPxRef.current = next;
      setOrbPx(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* On a phone the header (eyebrow, title, button) and the device are both
     absolutely placed. Measure the header and park the device a gap below it,
     so a banner or a wrapped title cannot drop the button onto the phone. */
  useEffect(() => {
    const pin = pinRef.current;
    const header = headerRef.current;
    if (!pin || !header) return undefined;
    const measure = () => {
      if (window.innerWidth > 991) {
        pin.style.removeProperty('--orb-stack');
        return;
      }
      const gap = 28;
      const top = header.getBoundingClientRect().bottom - pin.getBoundingClientRect().top + gap;
      pin.style.setProperty('--orb-stack', `${Math.round(top)}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [bannerPx]);
  /* phase-1 scene. 'glass' = the 3D glass marble (Version C) that condenses and
     MORPHS into the orb (desktop + phones); 'field' = the original full-screen
     marble field (?orbScene=field); reduced motion snaps to the formed end state. ?orbScene=field
     brings back the field; ?orbLook=original|middle|emerald picks the glass colours (live: middle).
     Read after mount so SSR markup stays stable. */
  const [scene, setScene] = useState<'glass' | 'field' | null>(null);
  const sceneRef = useRef<'glass' | 'field'>('field');
  const [glassLook, setGlassLook] = useState<GlassLook>('middle'); // the live look
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const compact = window.matchMedia('(max-width: 991px)');
    const sync = () => {
      const next = compact.matches || q.get('orbScene') === 'field' ? 'field' : 'glass';
      sceneRef.current = next;
      setScene(next);
    };
    sync();
    compact.addEventListener('change', sync);
    const lk = q.get('orbLook');
    if (lk === 'original' || lk === 'middle' || lk === 'emerald') setGlassLook(lk);
    return () => compact.removeEventListener('change', sync);
  }, []);

  const [active, setActive] = useState(0);
  // whether the orb is formed on the phone; drives the MATERIAL only (blue until
  // formed), while the label + metrics keep their cycled state
  const [formed, setFormed] = useState(false);

  // cycle the signal state — but ONLY while the orb is formed (progress ≥ FORMED)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => {
      if (progressRef.current >= FORMED) setActive((a) => (a + 1) % CYCLE.length);
    }, 4200);
    return () => clearInterval(id);
  }, []);

  // odometer roll: tween the displayed numbers toward the active state (slow),
  // writing straight to the DOM so it doesn't re-render every frame
  useEffect(() => {
    const targets = CYCLE[active].stats.map((s) => s.v);
    const start = [...displayedRef.current];
    const t0 = performance.now();
    const dur = 1400;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / dur);
      const e = easeOut(t);
      for (let i = 0; i < 3; i += 1) {
        const v = Math.round(lerp(start[i], targets[i], e));
        displayedRef.current[i] = v;
        const el = valRefs.current[i];
        if (el) el.textContent = String(v);
      }
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const apply = (pRaw: number) => {
      // phones show the assembled orb. The scroll morph's clip is not a circle
      // on a short viewport, which stretches the sphere into a pill.
      const compact = window.innerWidth <= 991;
      const p = compact ? 1 : pRaw;
      progressRef.current = p;
      // full-screen field + the morph keep the orb BLUE until formed; once
      // formed (and latched) the material cycles and stays cycled.
      const nowFormed = p >= FORMED;
      if (nowFormed !== formedRef.current) {
        formedRef.current = nowFormed;
        setFormed(nowFormed);
      }

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const orbR = orbPxRef.current / 2; // fixed final circle radius, px (matches sphere)

      const field = fieldRef.current;
      if (compact && field) {
        field.style.opacity = '1';
        field.style.clipPath = 'none';
        (field.style as unknown as { webkitClipPath: string }).webkitClipPath = 'none';
      } else if (field && sceneRef.current === 'glass') {
        // glass: the real orb waits, clipped to its circle, and appears the instant the
        // marble's morph has turned it into the orb's exact pixels (no dissolve)
        const ix = vw / 2 - orbR;
        const iy = (vh + 16) / 2 - orbR;
        const clip = `inset(${iy}px ${ix}px ${iy}px ${ix}px round ${orbR}px)`;
        field.style.clipPath = clip;
        (field.style as unknown as { webkitClipPath: string }).webkitClipPath = clip;
        field.style.opacity = p >= GLASS_TIMING.handoff ? '1' : '0';
      } else if (field) {
        field.style.opacity = '1';
        // the field stays a full-screen static marble through the headline reveal
        // + hold + fade (p < 0.46); then a gradual morph shrinks it into the orb
        const eX = easeInOut(seg(p, 0.46, 0.70));
        const eY = easeInOut(seg(p, 0.44, 0.70));
        const insetX = lerp(0, vw / 2 - orbR, eX);
        // the field is 16px taller than the viewport, so centre the clip circle
        // on the field's centre (= 8px below the viewport centre)
        const insetY = lerp(0, (vh + 16) / 2 - orbR, eY);
        const rad = orbR * easeInOut(seg(p, 0.48, 0.68));
        const clip = `inset(${insetY}px ${insetX}px ${insetY}px ${insetX}px round ${rad}px)`;
        field.style.clipPath = clip;
        (field.style as unknown as { webkitClipPath: string }).webkitClipPath = clip;
      }

      // sphere lighting resolves ONLY AFTER the clip is already the circle (which
      // completes at 0.70), so the sphere's shading edge lands exactly on the clip
      // edge — no second ring appearing inside a still-rectangular crop
      zoomRef.current = compact || sceneRef.current === 'glass' ? 0 : 1 - easeInOut(seg(p, 0.7, 0.84));

      const white = whiteRef.current;
      if (white) white.style.opacity = String(seg(p, 0.52, 0.68));

      // ── phase-1 headline: each word rises + sharpens; hold, then fade by 0.46 ──
      const words = wordRefs.current;
      const n = words.length || 1;
      const fade = easeInOut(seg(p, 0.36, 0.46));
      for (let i = 0; i < words.length; i += 1) {
        const el = words[i];
        if (!el) continue;
        const t0 = lerp(0.03, 0.22, i / n);
        const rev = easeOut(seg(p, t0, t0 + 0.12));
        const op = rev * (1 - fade);
        const y = lerp(34, 0, rev) - fade * 30;
        const blur = lerp(14, 0, rev);
        el.style.opacity = String(op);
        el.style.transform = `translateY(${y}px)`;
        el.style.filter = blur > 0.2 ? `blur(${blur}px)` : 'none';
      }

      const label = labelRef.current;
      if (label) label.style.opacity = String(seg(p, 0.78, 0.9));
      const explained = explainedRef.current;
      if (explained) {
        const ex = seg(p, 0.68, 0.88);
        explained.style.opacity = String(ex);
        explained.style.transform = `translateY(${lerp(20, 0, ex)}px)`;
      }
    };

    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const compactMq = window.matchMedia('(max-width: 991px)');
    if (reduce) {
      apply(1);
      return undefined;
    }

    const compute = () => {
      const r = track.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return clamp01(-r.top / (total || 1));
    };

    let raf = 0;
    let cur = compute();
    let running = false;
    /* Follow the scroll with a light ease. A fast flick still moves the beat;
       it does not get held back by a speed cap. */
    const tick = () => {
      if (compactMq.matches) {
        apply(1);
        running = false;
        return;
      }
      const r = track.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const t = clamp01(-r.top / (total || 1));
      cur += (t - cur) * 0.14;
      if (Math.abs(t - cur) < 0.0004) cur = t;
      apply(cur);
      raf = requestAnimationFrame(tick);
    };
    apply(compactMq.matches ? 1 : cur);

    const onCompact = () => {
      if (compactMq.matches) {
        running = false;
        cancelAnimationFrame(raf);
        apply(1);
        return;
      }
      const r = track.getBoundingClientRect();
      cur = compute();
      apply(cur);
      const visible = r.bottom > 0 && r.top < window.innerHeight;
      if (visible && !running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };
    compactMq.addEventListener('change', onCompact);

    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          if (!running) {
            running = true;
            raf = requestAnimationFrame(tick);
          }
        } else {
          running = false;
          cancelAnimationFrame(raf);
        }
      },
      { threshold: 0 },
    );
    io.observe(track);

    return () => {
      compactMq.removeEventListener('change', onCompact);
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  const rows = [0, 1, 2].map((r) => topics.filter((_, i) => i % 3 === r));
  const cur = CYCLE[active];
  // material is BLUE until the orb is formed on the phone; then it takes the
  // cycled state's colour (and eases back to blue when scrolled away)
  const orbState: OrbState = useMemo(
    () =>
      formed
        ? { ...BASE_ORB, ...cur.orb, tint: (cur.orb.tint ?? BASE_ORB.tint) as RGB }
        : BASE_ORB,
    [cur, formed],
  );

  return (
    <section
      className={styles.track}
      ref={trackRef}
      aria-label={t('science.landing.orb.sectionLabel')}
      style={{ '--banner-px': `${bannerPx}px` } as CSSProperties}
    >
      <div className={styles.pin} ref={pinRef}>
        <div className={styles.white} ref={whiteRef} aria-hidden="true" />

        {scene === 'glass' && (
          <GlassMarble
            key={`glass-${glKey}`}
            progressRef={progressRef}
            getOrbCanvas={() => fieldRef.current?.querySelector('canvas') ?? null}
            onUnavailable={() => {
              sceneRef.current = 'field';
              setScene('field');
            }}
            look={glassLook}
            orbPx={orbPx}
            className={styles.glass}
          />
        )}

        {/* the orb's shader as a full-bleed field, clipped rect → card → circle */}
        <div className={styles.field} ref={fieldRef} aria-hidden="true">
          <SphereOrb
            key={`${orbPx}-${glKey}`}
            /* readable from the first mount (the glass marble morphs into its pixels);
               must not depend on `scene` — a remount here loses the WebGL context */
            readable
            state={orbState}
            zoomRef={zoomRef}
            fixedDiameterPx={orbPx}
            className={styles.fieldCanvas}
            aria-label={t('science.landing.orb.signalOrb')}
          />
        </div>

        <h2 className={styles.headline} ref={headlineRef} aria-label={`${line1} ${line2}`}>
          {headlineWords.map((w, i) => [
            i === headlineBreakAt ? <br key="br" className={styles.hlBreak} /> : null,
            <span
              key={`${w}-${i}`}
              className={styles.hlWord}
              ref={(el) => {
                wordRefs.current[i] = el;
              }}
              aria-hidden="true"
            >
              {w}
            </span>,
          ])}
        </h2>

        <span className={styles.orbLabel} ref={labelRef}>
          <span key={active} className={styles.orbLabelText}>
            {orbLabel[cur.labelKey]}
          </span>
        </span>

        {/* the "explained simply" chrome assembling around the orb */}
        <div className={styles.explained} ref={explainedRef}>
          <div className={styles.pills} aria-hidden="true">
            {rows.map((row, ri) =>
              row.length ? (
                <Marquee
                  key={ri}
                  className={styles.pillRow}
                  direction={ri % 2 ? 'right' : 'left'}
                  speed={16 + ri * 5}
                  gradient={false}
                >
                  {row.map((t) => (
                    <Link
                      key={t.slug}
                      href={`/science/explainers/${t.slug}/`}
                      className={styles.pill}
                      tabIndex={-1}
                    >
                      {t.label}
                    </Link>
                  ))}
                </Marquee>
              ) : null,
            )}
          </div>

          <div className={styles.device} aria-hidden="true" />

          <header className={styles.header} ref={headerRef}>
            <p className={styles.eyebrow}>{t('science.landing.orb.eyebrow')}</p>
            <h3 className={styles.heading}>{t('science.landing.orb.heading')}</h3>
            <ButtonV2
              href="/science/explainers/"
              variant="black"
              label={t('science.landing.orb.readExplainers')}
            />
          </header>

          <div className={styles.statsBand}>
            <div className={styles.statsRow}>
              {cur.stats.map((s, i) => (
                <div key={s.k} className={styles.stat}>
                  <span className={styles.statK}>{statLabel[s.k]}</span>
                  <span className={styles.statV}>
                    {/* number text is owned by the odometer tween (writes
                        textContent directly), not by React, so it never flashes
                        the target before rolling */}
                    <span
                      className={styles.statNum}
                      ref={(el) => {
                        valRefs.current[i] = el;
                        if (el && !el.textContent) el.textContent = String(displayedRef.current[i]);
                      }}
                    />
                    <Tri dir={s.dir} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
